import React, { useState } from 'react';
import { supabase } from '../../supabaseClient';
import { sendApplicationNotification } from '../../utils/applicationNotifications';
import './Career.css';
import PositionsSection from '../Pvg/PositionsSection';

const Career = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    message: '',
    resume: null
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const openModal = (position) => {
    setSelectedPosition(position);
    setFormData(prev => ({ ...prev, position }));
    setShowModal(true);
    setSubmitStatus('');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPosition('');
    setFormData({
      name: '',
      email: '',
      phone: '',
      position: '',
      experience: '',
      message: '',
      resume: null
    });
    setLoading(false);
    setUploading(false);
    setSubmitStatus('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({ ...prev, resume: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSubmitStatus('submitting');
    
    console.log('📝 Starting application submission...');
    
    try {
      let resumeUrl = null;
      
      if (formData.resume) {
        setUploading(true);
        console.log('📄 Uploading resume...');
        
        if (formData.resume.size > 5 * 1024 * 1024) {
          alert('File size too large. Please upload a file smaller than 5MB.');
          setLoading(false);
          setUploading(false);
          setSubmitStatus('error');
          return;
        }

        const fileExt = formData.resume.name.split('.').pop();
        const fileName = `${Math.random()}-${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(fileName, formData.resume);
        
        if (uploadError) {
          console.error('Upload error:', uploadError);
          throw new Error(`File upload failed: ${uploadError.message}`);
        }
        
        const { data: urlData } = supabase.storage
          .from('resumes')
          .getPublicUrl(fileName);
        
        resumeUrl = urlData.publicUrl;
        console.log('✅ Resume uploaded. URL:', resumeUrl);
        setUploading(false);
      }

      console.log('💾 Saving to database...');
      const { data, error } = await supabase
        .from('applications')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            position: formData.position,
            experience: formData.experience,
            message: formData.message,
            resume_url: resumeUrl,
            status: 'new',
            created_at: new Date().toISOString()
          }
        ])
        .select();
      
      if (error) {
        console.error('Database error:', error);
        throw new Error(`Database operation failed: ${error.message}`);
      }
      
      console.log('✅ Database save successful:', data);

      const notificationSent = await sendApplicationNotification({
        applicationId: data?.[0]?.id,
        applicantName: formData.name,
        applicantEmail: formData.email,
        positionName: formData.position || selectedPosition,
        resumeUrl,
        phone: formData.phone,
        experience: formData.experience,
        message: formData.message,
      });

      if (!notificationSent) {
        console.warn('⚠️ Application email notification failed.');
      }

      setSubmitStatus('success');
      console.log('🎉 Application submitted successfully!');
      
      setTimeout(() => {
        closeModal();
      }, 2000);
      
    } catch (error) {
      console.error('❌ Error details:', error);
      setSubmitStatus('error');
      alert(`There was an error submitting your application: ${error.message}. Please try again or contact us directly.`);
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  return (
    <>
      <section id="careers" className="careers">
        <div className="container">
          <div className="section-title">
            <h2>Join Our Team</h2>
            <p>We're always looking for talented individuals to join our growing team of innovators.</p>
          </div>
          
          <div className="careers-hero">
            <div className="careers-hero-content">
              <h3>Shape the Future With Us</h3>
              <p>At Prime Ventures Global we don't just offer jobs - we offer opportunities to grow, learn, and make a real impact. Join our mission to transform businesses through innovation.</p>
              <div className="careers-stats">
                <div className="stat">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Team Members</span>
                </div>
                <div className="stat">
                  <span className="stat-number">15+</span>
                  <span className="stat-label">Countries</span>
                </div>
                <div className="stat">
                  <span className="stat-number">96%</span>
                  <span className="stat-label">Employee Satisfaction</span>
                </div>
              </div>
            </div>
            <div className="careers-hero-image">
              <div className="floating-card">
                <i className="fas fa-award"></i>
                <h4>Best Workplace 2023</h4>
                <p>Recognized for excellence in employee culture</p>
              </div>
            </div>
          </div>
          
          <div className="benefits-section">
            <h3>Why Choose Us</h3>
            <div className="benefits-grid">
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-star"></i>
                </div>
                <h4>World-Class Service</h4>
                <p>Every vertical is backed by quality and expertise.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h4>Simplicity & Ease</h4>
                <p>Book, order, or learn—just a few clicks away.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-globe-americas"></i>
                </div>
                <h4>Trusted Global Reach</h4>
                <p>UK-based, serving customers and businesses worldwide.</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-graduation-cap"></i>
                </div>
                <h4>Learning & Development</h4>
                <p>Continuous learning opportunities with annual training budget and mentorship programs</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-users"></i>
                </div>
                <h4>Collaborative Culture</h4>
                <p>Work with talented professionals in a supportive, inclusive environment</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-trophy"></i>
                </div>
                <h4>Career Growth</h4>
                <p>Clear career paths, quarterly reviews, and advancement opportunities</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-heartbeat"></i>
                </div>
                <h4>Health & Wellness</h4>
                <p>Comprehensive health insurance, mental health support, and fitness benefits</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-laptop-house"></i>
                </div>
                <h4>Flexible Work</h4>
                <p>Remote work options, flexible hours, and work-life balance focus</p>
              </div>
              
              <div className="benefit-item">
                <div className="benefit-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <h4>Generous Time Off</h4>
                <p>Unlimited PTO, paid holidays, and extended parental leave policies</p>
              </div>
            </div>
          </div>
          
          {/* <div className="positions-section">
            <div className="section-header">
              <h3>Open Positions</h3>
              <p>Ready to take the next step in your career? Explore our current opportunities.</p>
            </div>
            
            <div className="filters">
              <button className="filter-btn active">All</button>
              <button className="filter-btn">Culinary</button>
            </div>
            
            <div className="positions-grid">
              <div className="position-card">
                <div className="position-header">
                  <h4>Freelance Cook</h4>
                  <span className="position-tag">Remote</span>
                </div>
                <div className="position-details">
                  <span><i className="fas fa-briefcase"></i> Full-time/Part-time</span>
                  <span><i className="fas fa-map-marker-alt"></i> Flexible Location</span>
                  <span><i className="fas fa-clock"></i> Posted 2 days ago</span>
                </div>
                <p>We're looking for passionate cooks to create amazing dishes for our clients. Showcase your culinary skills and build your reputation.</p>
                <div className="position-skills">
                  <span className="skill-tag">Vegetarian</span>
                  <span className="skill-tag">Non-Vegetarian</span>
                  <span className="skill-tag">Street Food</span>
                  <span className="skill-tag">Chinese</span>
                  <span className="skill-tag">Regional Cuisine</span>
                </div>
                <button 
                  onClick={() => openModal('Freelance Cook')} 
                  className="btn btn-primary"
                >
                  Apply Now
                </button>
              </div>
            </div>
          </div> */}
          <PositionsSection/>
        </div>
      </section>

      {/* Apply Now Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="pvg-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="pvg-modal-header">
              <h3>Apply for {selectedPosition}</h3>
              <button 
                className="modal-close" 
                onClick={closeModal} 
                disabled={loading}
                aria-label="Close modal"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            {submitStatus === 'success' ? (
              <div className="submit-success">
                <div className="success-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <h4>Application Submitted Successfully!</h4>
                <p>Thank you for applying to the position. We'll review your application and get back to you soon.</p>
                <button onClick={closeModal} className="btn btn-primary">
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="application-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Enter your full name"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                    placeholder="Enter your email address"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={loading}
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="experience">Years of Experience</label>
                  <select
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="">Select experience level</option>
                    <option value="0-1">0-1 years</option>
                    <option value="1-3">1-3 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5+">5+ years</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Why are you interested in this position?</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    disabled={loading}
                    placeholder="Tell us why you're interested in this position and what makes you a good fit..."
                  ></textarea>
                </div>
                
                <div className="form-group">
                  <label htmlFor="resume">Upload Resume (PDF, DOC, DOCX) *</label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx"
                    required
                    disabled={loading}
                  />
                  <small>Max file size: 5MB. Accepted formats: PDF, DOC, DOCX</small>
                  {formData.resume && (
                    <div className="file-selected">
                      <i className="fas fa-check"></i> Selected: {formData.resume.name}
                    </div>
                  )}
                </div>
                
                <div className="form-actions">
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="btn btn-outline" 
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin"></i>
                        {uploading ? 'Uploading Resume...' : 'Submitting Application...'}
                      </>
                    ) : (
                      'Submit Application'
                    )}
                  </button>
                </div>
                
                {submitStatus === 'error' && (
                  <div className="error-message">
                    <i className="fas fa-exclamation-triangle"></i>
                    There was an error submitting your application. Please try again.
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Career;
